
import React, { useState } from 'react';
import { Mail, Phone } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import InputField from '../ui/form/InputField';
import PasswordInput from '../ui/form/PasswordInput';
import TermsCheckbox from '../ui/form/TermsCheckbox';

interface RegisterFormProps {
  onSubmit: (email: string, phone: string, password: string) => void;
  onLoginClick: () => void;
}

const RegisterForm: React.FC<RegisterFormProps> = ({ onSubmit, onLoginClick }) => {
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [errors, setErrors] = useState({ 
    email: '', 
    phone: '', 
    password: '', 
    confirmPassword: '',
    terms: ''
  });

  const validateForm = () => {
    let valid = true;
    const newErrors = { 
      email: '', 
      phone: '', 
      password: '', 
      confirmPassword: '',
      terms: ''
    };

    if (email && !/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Email is invalid';
      valid = false;
    }

    if (phone && !/^\+?[1-9]\d{9,14}$/.test(phone)) {
      newErrors.phone = 'Phone number is invalid';
      valid = false;
    }

    if (password && password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
      valid = false;
    }

    if (password && confirmPassword && password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSubmit(email, phone, password);
      toast({
        title: "Registration successful",
        description: "Please check your email to verify your account",
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex-1 px-4">
      <InputField
        id="email"
        label="Email Address"
        type="email"
        placeholder="you@example.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        error={errors.email}
        icon={<Mail className="w-5 h-5 text-gray-400" />}
      />

      <InputField
        id="phone"
        label="Phone Number"
        type="tel"
        placeholder="+1234567890"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        error={errors.phone}
        icon={<Phone className="w-5 h-5 text-gray-400" />}
      />

      <PasswordInput
        id="password"
        label="Password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        error={errors.password}
      />

      <PasswordInput
        id="confirm-password"
        label="Confirm Password"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        error={errors.confirmPassword}
      />

      <TermsCheckbox
        checked={acceptTerms}
        onChange={(e) => setAcceptTerms(e.target.checked)}
        error={errors.terms}
      />

      <button type="submit" className="btn-primary mb-4">
        Create Account
      </button>

      <div className="text-center">
        <p className="text-sm text-gray-600">
          Already have an account?{' '}
          <button
            type="button"
            className="text-neobank-primary font-medium"
            onClick={onLoginClick}
          >
            Log In
          </button>
        </p>
      </div>
    </form>
  );
};

export default RegisterForm;
